import React, { useState, useEffect, useMemo, useRef } from "react";
import "./DataSubmitter.css";
import { isEntryLocked, readResponses, writeSheetData } from "../../shared/utils/googleSheetsApi";
import { Box, TextField, IconButton, ThemeProvider, CssBaseline } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { API_BASE } from '../../config';
import { getAppTheme } from '../../theme.js';


// DataSubmitter manages the score submission UI and logic for a single entry.
// It now includes all score input, validation, and layout logic.
function DataSubmitter({ user, rowIndex, scoreData = [], onNext, judgeResponses, setJudgeResponses, colIndexes, judgesColIndexes }) {
  const { titleIdx, levelIdx, styleIdx, sizeIdx, schoolIdx, lockedIdx } = colIndexes;
  const { artisticIdx, technicalIdx, notesIdx, nomIdx } = judgesColIndexes; // from judgesColIndexes
  const userEmail = user?.email || "";
  const title = scoreData[rowIndex]?.[titleIdx] || "";
  const level = scoreData[rowIndex]?.[levelIdx] || "";
  const style = scoreData[rowIndex]?.[styleIdx] || "";
  const size = scoreData[rowIndex]?.[sizeIdx] || "";
  const school = scoreData[rowIndex]?.[schoolIdx] || "";
  const [artistic, setArtistic] = useState("");
  const [technical, setTechnical] = useState("");
  const [notes, setNotes] = useState("");
  const [locked, setLocked] = useState(false);
  const [disableArtistic, setDisableArtistic] = useState(false);
  const [disableTechnical, setDisableTechnical] = useState(false);
  const [restrictionMessage, setRestrictionMessage] = useState("");
  const [showLockedDialog, setShowLockedDialog] = useState(false);
  const [artisticError, setArtisticError] = useState("");
  const [technicalError, setTechnicalError] = useState("");
  const [nominated, setNominated] = useState(false);
  const isFirstRender = useRef(true);

  const [mode] = useState(
    localStorage.getItem('themeMode') ||
    (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  );

  const theme = useMemo(() => getAppTheme(mode), [mode]);

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const entryNumber = useMemo(() => {
    if (!Array.isArray(scoreData) || typeof rowIndex !== 'number') {
      console.log("Invalid scoreData or rowIndex for deriving entryNumber:", { rowIndex });
      return 1;
    }
    const row = scoreData[rowIndex];
    if (!row || row.length === 0) {
      return 1;
    }
    return row[0]; // returns string; convert to Number(...) where needed
  }, [rowIndex, scoreData]);

  // Fetch data for this user and entry
  useEffect(() => {
    function applyFound(found) {
      setArtistic(found?.[artisticIdx] ?? ""); //fourth column is artistic
      setTechnical(found?.[technicalIdx] ?? ""); //fifth column is technical
      setNotes(found?.[notesIdx] ?? ""); //sixth column is notes
      setNominated((found?.[nomIdx] || "").toLowerCase() === "true"); //seventh column is nomination status (assumes "true"/"false" strings)
    }

    async function fetchData() {
      if (!rowIndex) return;

      // If judgeResponses prop is provided and has data, use it
      if (judgeResponses && Array.isArray(judgeResponses) && judgeResponses.length > 1) {
        const rows = judgeResponses.slice(1); // skip header
        const found = rows.find(row => String(row[1]) === String(entryNumber));
        if (found) {
          applyFound(found);
          return;
        }
        // if not found in provided responses, clear fields
        setArtistic("");
        setTechnical("");
        setNotes("");
        setNominated(false);
        return;
      }

      // Fallback: fetch from backend only if judgeResponses not available
      if (!userEmail) return;

      setArtistic("");
      setTechnical("");
      setNotes("");
      setNominated(false);

      try {
        const data = await readResponses(API_BASE, "Responses");
        // Skip header row if present
        const dataRows = data.length > 0 && (data[0][0] === 'email' || data[0][0] === 'Email') ? data.slice(1) : data;
        // Find the row for this user and entry
        const found = dataRows.find((row) =>
          row[0] === userEmail && String(row[1]) === String(entryNumber)
        );
        if (found) {
          applyFound(found);
        }
      } catch (e) {
        if (e && (e.status === 401)) {
          // Session expired - redirect to login
          window.location.href = `${API_BASE}/auth/google`;
          return;
        }
        console.error("Failed to fetch responses for user", e);
      }
    }
    fetchData();
  }, [entryNumber, scoreData, userEmail, judgeResponses, rowIndex, artisticIdx, technicalIdx, notesIdx, nomIdx]);

  // Compute entry information (KPOP, FREESTYLE, CC, locked, etc.) from sheet data
  useEffect(() => {
    let infoMsg = "";
    let infoClass = "ossdf-information-message";

    setDisableArtistic(false);
    setDisableTechnical(false);

    if (!entryNumber || !scoreData.length) {
      setRestrictionMessage("");
      return;
    }

    //Get the specific row for this entry number
    const row = scoreData.find(r => String(r[0]) === String(entryNumber));
    // const getColIndex = (colName) => {
    //   if (!header || !header.length) return undefined;
    //   return header.findIndex(h => String(h).trim().toLowerCase() === colName.toLowerCase());
    // };

    //check if entry is locked
    setLocked(false);
    if (lockedIdx !== undefined && row && row[lockedIdx] && String(row[lockedIdx]).toLowerCase() === "true") {
      setLocked(true);
      infoMsg = "This entry is locked and cannot be modified. Please contact the tabulator for any further changes to this entry.";
      infoClass = "ossdf-information-message locked";
      setRestrictionMessage({ text: infoMsg, className: infoClass });
      return;
    }

    //check dance style and level for restrictions
    // const levelIdx = header.findIndex(h => String(h).trim().toLowerCase().includes('level'));
    // const danceStyleIdx = header.findIndex(h => String(h).trim().toLowerCase().includes('style'));
    const level = row && row[levelIdx] ? String(row[levelIdx]).toUpperCase() : '';
    const danceStyle = row && row[styleIdx] ? String(row[styleIdx]).toUpperCase() : '';
    if (danceStyle === "CREATIVE COLLABORATION" || level === "FREESTYLE") {
      setDisableArtistic(true);
      setDisableTechnical(true);
      infoMsg = "There are no scores for this type of entry.";
    } else if (danceStyle === "KPOP" || danceStyle.toLowerCase().includes("set")) {
      setDisableArtistic(true);
      setDisableTechnical(false);
      infoMsg = "Not original choreography; only enter a Technical Score.";
    } else {
      setDisableArtistic(false);
      setDisableTechnical(false);
      infoMsg = "";
    }
    setRestrictionMessage(infoMsg ? { text: infoMsg, className: "ossdf-information-message" } : "");
  }, [entryNumber, scoreData, levelIdx, lockedIdx, styleIdx]);

  // Input change handlers with validation for numbers
  const handleArtisticChange = (e) => {
    const val = e.target.value;
    if (/^\d*\.?\d*$/.test(val)) {
      setArtistic(val);
      let num = Number(val);
      if (
        val !== "" && val !== "." && !isNaN(num) && ((num > 10 && num < 80) || num > 100)
      ) {
        setArtisticError("Score must be between 80 and 100");
      } else {
        setArtisticError("");
      }
    }
  };
  const handleTechnicalChange = (e) => {
    const val = e.target.value;
    if (/^\d*\.?\d*$/.test(val)) {
      setTechnical(val);
      let num = Number(val);
      if (
        val !== "" && val !== "." && !isNaN(num) && ((num > 10 && num < 80) || num > 100)
      ) {
        setTechnicalError("Score must be between 80 and 100");
      } else {
        setTechnicalError("");
      }
    }
  };

  /* Must have valid number to submit */
  const canSubmit =
    !!userEmail &&
    !locked &&
    !artisticError &&
    !technicalError &&
    (
      (disableArtistic && !isNaN(Number(technical)) && Number(technical) >= 80 && Number(technical) <= 100)
      ||
      (!isNaN(Number(artistic)) && !isNaN(Number(technical)) &&
        Number(artistic) >= 80 && Number(artistic) <= 100 &&
        Number(technical) >= 80 && Number(technical) <= 100)
      ||
      (disableArtistic && disableTechnical && !locked)  // allow submit if both are disabled (e.g. for freestyle/CC with notes only)
    );

  // Submit scores to backend (write to Responses sheet)
  const handleSubmit = async () => {
    try {
      // Check if entry is locked BEFORE attempting to write
      const data = await isEntryLocked(API_BASE, entryNumber);
      if (data && data.locked) {
        setLocked(true);
        setShowLockedDialog(true);
        return; // Do not submit if locked
      }

      // Prepare the row: [email, entryNumber, title, level, style, size, artistic, technical, notes, nominated]
      const row = [
        userEmail,
        entryNumber === "" || isNaN(Number(entryNumber)) ? "" : Number(entryNumber),
        title,
        level,
        style,
        size,
        school,
        artistic === "" || isNaN(Number(artistic)) ? "" : Number(artistic),
        technical === "" || isNaN(Number(technical)) ? "" : Number(technical),
        notes || "",
        nominated ? "true" : "" // Store nomination status as "true"/"false" string
      ];
      await writeSheetData({ row, sheet: "Responses", API_BASE });
      // Update local judgeResponses if available to avoid refetching immediately
      if (setJudgeResponses) {
        setJudgeResponses((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) return [row];

          const header = prev[0];
          const body = prev.slice(1);

          const existingIndex = body.findIndex(
            (r) => String(r[1]) === String(row[1]) // entry number column
          );

          if (existingIndex >= 0) {
            const nextBody = [...body];
            nextBody[existingIndex] = row; // overwrite existing response for this entry
            return [header, ...nextBody];
          }

          return [header, ...body, row]; // append new response
        });
      }


      setArtistic("");
      setTechnical("");
      setNotes("");
      setNominated(false);

      if (onNext) onNext();
    } catch (e) {
      if (e && (e.status === 401)) {
        // Session expired - redirect to login
        window.location.href = `${API_BASE}/auth/google`;
        return;
      }

      if (e && (e.status === 403 || e.status === 'locked')) {
        setLocked(true);
        setShowLockedDialog(true);
        return;
      }
      else {
        console.error("Failed to submit scores", e);
        alert("Failed to submit scores. Please try again.");
      }
    }
  };

  const entryAlreadySubmitted = useMemo(() => {
    if (!judgeResponses || judgeResponses.length <= 1) return false;
    const rows = judgeResponses.slice(1);
    return rows.some(row => String(row[1]) === String(entryNumber));
  }, [judgeResponses, entryNumber]);

  const handleNominate = () => {
    const newNominated = !nominated;
    setNominated(newNominated);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Entry not yet in sheet — nomination will be saved with handleSubmit
    if (!entryAlreadySubmitted) {
      return;
    }

    async function updateNomination() {
      try {
        const row = [
          userEmail,
          entryNumber === "" || isNaN(Number(entryNumber)) ? "" : Number(entryNumber),
          title,
          level,
          style,
          size,
          school,
          artistic === "" || isNaN(Number(artistic)) ? "" : Number(artistic),
          technical === "" || isNaN(Number(technical)) ? "" : Number(technical),
          notes || "",
          newNominated ? "true" : "" // Store nomination status as "true"/"false" string
        ];
        await writeSheetData({ row, sheet: "Responses", API_BASE });
      } catch (e) {
        console.error('Failed to update nomination status', e);
        // Revert the toggle if the write failed
        setNominated(newNominated);
      }
    }
    updateNomination();
  };

  return (
    <ThemeProvider theme={{ theme }}>
      <CssBaseline />
      <div>
        {/* Single top bar for information or locked message */}
        {restrictionMessage && restrictionMessage.text && (
          <div className={restrictionMessage.className} style={{ whiteSpace: 'pre-line' }}>
            {locked && <LockIcon fontSize="small" />} {restrictionMessage.text}</div>
        )}
        {/* Score input fields and restrictions now handled here */}
        <Box className='ossdf-datasubmitter-row'>
          {/* Left: Artistic above Technical */}
          <Box className='ossdf-score-input-column'>
            <TextField
              label="Artistic Score"
              type="text"
              autoComplete="off"
              value={artistic}
              onChange={handleArtisticChange}
              error={!!artisticError}
              helperText={artisticError ? artisticError : " "} // Always reserve space
              slotProps={{ formHelperText: { sx: { fontSize: '0.7rem' } } }}
              fullWidth
              disabled={disableArtistic || locked}
              sx={{ marginBottom: 0.3 }} //margin bottom
            />
            <TextField
              label="Technical Score"
              type="text"
              autoComplete="off"
              value={technical}
              onChange={handleTechnicalChange}
              error={!!technicalError}
              helperText={technicalError ? technicalError : " "} // Always reserve space
              slotProps={{ formHelperText: { sx: { fontSize: '0.7rem' } } }}
              fullWidth
              disabled={disableTechnical || locked}
              sx={{ marginBottom: 0.3, marginTop: 0 }} //margin bottom
            />
          </Box>
          {/* Right: Notes field and submit button */}
          <Box className='ossdf-notes-and-submit'>
            <TextField
              label="Notes - for judges ref only, not visible to dancers"
              multiline
              autoComplete="off"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              variant="outlined"
              fullWidth
              disabled={locked}
            />
            <Box className="ossdf-submit-wrap">
              <Box className="ossdf-judges-choice" onClick={() => handleNominate() }
                title={nominated ? "Remove nomination" : "Nominate for Judges' Choice"}
                sx={{ cursor: 'pointer' }}
              >
                <IconButton
                  size="large"
                  sx={{ color: nominated ? '#EF9F27' : 'inherit', padding: 0 }}
                >
                  {nominated
                    ? <StarIcon sx={{ fontSize: '1.5rem' }} />
                    : <StarBorderIcon sx={{ fontSize: '1.5rem' }} />
                  }
                </IconButton>
                Judges' Choice
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSubmit}
                disabled={!canSubmit}
                sx={{ minWidth: 120, marginLeft: 'auto' }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
        {/* Locked dialog popup */}
        <Dialog open={showLockedDialog} onClose={() => setShowLockedDialog(false)}>
          <DialogTitle>Entry Locked</DialogTitle>
          <DialogContent>
            This entry has been locked and your score was not updated.<br />
            Please contact the tabulator for any further changes to this entry.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setShowLockedDialog(false); }} color="primary" autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
}

export default DataSubmitter;
